/**
 * O atalho de negócio DIGITADO no composer — `/pré-reunião Ilo Santos`.
 *
 * spec-001 `project-home-ia-skills/preparacao-reuniao`, G-2 do QA de tela.
 *
 * ## Por que digitar precisa funcionar
 *
 * Quando `/pré-reunião` sai sem cliente, o próprio backend responde *"Mande
 * /pré-reunião com o nome do cliente (por exemplo: /pré-reunião Marcelo
 * Pontes)"* — e o contrato dele já tem o campo para isso: `ComandoDeAtalho.
 * argumento` (`contratos/entrada.py`) é documentado com ESSE exemplo. Até o
 * QA de 19/09/2026 a tela não deixava cumprir a instrução: a barra abria o
 * menu de ferramentas, que não conhece atalho nenhum, e o texto saía como
 * pergunta livre (ou nem saía). Esta função é a tradução que faltava —
 * `texto digitado` → o mesmo `{slug, argumento}` que o CLIQUE no atalho
 * monta.
 *
 * ## Pura, e usada dos DOIS lados
 *
 * `view-chat.vue` a usa para DESPACHAR (o texto vira comando em vez de
 * pergunta) e `send-message.vue` para DECIDIR O MENU (não anunciar "Nenhuma
 * ferramenta com esse nome" sobre um atalho que existe). Uma função só: duas
 * leituras do que conta como atalho digitado divergiriam no primeiro atalho
 * novo, e o sintoma seria a tela dizer uma coisa e o envio fazer outra.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
import { normalizeText } from '@utils/Format'

// A mesma barra do menu de ferramentas (`send-message.vue`). Aqui ela também
// faz parte do NOME do atalho: o catálogo devolve `label: '/pré-reunião'`.
const PREFIXO_DE_ATALHO = '/'

/**
 * As grafias que casam com uma ação de atalho, normalizadas.
 *
 * Duas, porque as duas são estáveis e podem divergir: o `label` do catálogo
 * (`/pré-reunião`, com acento, que é o que a tela mostra) e o `slug`
 * (`pre-reuniao`, que é o que viaja no corpo). Quem digita copia o que leu na
 * tela; quem vem de outro chat digita o slug.
 */
function grafiasDaAcao(acao) {
  const comando = acao && acao.comando
  if (!comando || !comando.slug) return []
  const grafias = [normalizeText(PREFIXO_DE_ATALHO + comando.slug)]
  if (acao.titulo) grafias.push(normalizeText(acao.titulo))
  return grafias
}

/**
 * O atalho que o texto digitado invoca, ou `null`.
 *
 * @param {String} texto o conteúdo do composer.
 * @param {Array} acoes as ações do módulo de atalhos (`acao.comando.slug` e
 *   `acao.titulo`), como `moduloDeAtalhosDeNegocio` as monta.
 * @returns {Object|null} `{slug, argumento, label}` no formato que
 *   `view-chat.vue::usarComando` já consome — `argumento` é `null` (nunca
 *   `''`) quando nada foi digitado depois do slug, porque é assim que o
 *   contrato do servidor distingue "não informou" de "informou vazio".
 */
export function atalhoDigitado(texto, acoes) {
  const valor = String(texto == null ? '' : texto)
  if (valor.indexOf(PREFIXO_DE_ATALHO) !== 0) return null

  const lista = Array.isArray(acoes) ? acoes : []
  if (!lista.length) return null

  // O slug é o PRIMEIRO token — o resto é argumento. `search(/\s/)` e não
  // `split(' ')`: o nome do cliente tem espaços, e quebrar em todos devolveria
  // só o primeiro nome.
  const corte = valor.search(/\s/)
  const digitado = corte === -1 ? valor : valor.slice(0, corte)
  const argumento = corte === -1 ? '' : valor.slice(corte).trim()

  const alvo = normalizeText(digitado)
  if (!alvo) return null

  const acao = lista.find(function (item) {
    return grafiasDaAcao(item).indexOf(alvo) !== -1
  })
  if (!acao) return null

  return {
    slug: acao.comando.slug,
    argumento: argumento || null,
    // O texto da bolha é o que a pessoa escreveu — nunca o rótulo do
    // catálogo, que perderia o nome do cliente que ela acabou de digitar.
    label: valor.trim(),
  }
}

export { PREFIXO_DE_ATALHO }
